import { useState, useEffect, useCallback } from 'react';
import { courrierService } from '../services/api';
import {
  Courrier,
  CourrierFilters,
  CourrierListResponse,
  CreateCourrierRequest,
  UpdateCourrierRequest,
  AssignCourrierRequest,
  TransitionCourrierRequest,
} from '../types/api';

interface UseCourriersReturn {
  courriers: Courrier[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  isLoading: boolean;
  error: string | null;
  createCourrier: (data: CreateCourrierRequest) => Promise<Courrier>;
  updateCourrier: (id: number, data: UpdateCourrierRequest) => Promise<Courrier>;
  deleteCourrier: (id: number) => Promise<void>;
  assignCourrier: (id: number, data: AssignCourrierRequest) => Promise<Courrier>;
  transitionCourrier: (id: number, data: TransitionCourrierRequest) => Promise<Courrier>;
  refreshCourriers: () => Promise<void>;
  searchCourriers: (query: string, filters?: CourrierFilters) => Promise<void>;
  loadMore: () => Promise<void>;
  reset: () => void;
}

interface UseCourriersOptions {
  filters?: CourrierFilters;
  page?: number;
  limit?: number;
  autoLoad?: boolean;
}

export function useCourriers(options: UseCourriersOptions = {}): UseCourriersReturn {
  const {
    filters: initialFilters,
    page: initialPage = 1,
    limit: initialLimit = 20,
    autoLoad = true,
  } = options;

  const [courriers, setCourriers] = useState<Courrier[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(initialPage);
  const [limit] = useState(initialLimit);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<CourrierFilters | undefined>(initialFilters);

  const loadCourriers = useCallback(async (pageNum: number = 1, currentFilters?: CourrierFilters) => {
    try {
      setIsLoading(true);
      setError(null);

      console.log('🔍 [USECOURRIERS] Chargement des courriers, page:', pageNum, 'filtres:', currentFilters);

      const response: CourrierListResponse = await courrierService.getCourriers(
        currentFilters,
        pageNum,
        limit,
      );

      console.log('✅ [USECOURRIERS] Réponse reçue:', {
        total: response.total,
        count: response.data?.length || 0,
        data: response.data,
        page: response.page,
        totalPages: response.totalPages
      });

      setCourriers(response.data || []);
      setTotal(response.total || 0);
      setTotalPages(response.totalPages || 0);
      setPage(response.page || 1);

      console.log('📊 [USECOURRIERS] État après mise à jour:', {
        courriersCount: response.data?.length || 0,
        total: response.total || 0,
        isLoading: false
      });
    } catch (err: any) {
      console.error('❌ [USECOURRIERS] Erreur lors du chargement des courriers:', err);
      console.error('❌ [USECOURRIERS] Erreur details:', err.response || err.message);
      setError(err.message || 'Erreur lors du chargement des courriers');
    } finally {
      setIsLoading(false);
    }
  }, [limit]);

  const refreshCourriers = useCallback(async () => {
    await loadCourriers(1, filters);
  }, [loadCourriers, filters]);

  const searchCourriers = useCallback(async (query: string, searchFilters?: CourrierFilters) => {
    const combinedFilters = { ...searchFilters, q: query };
    setFilters(combinedFilters);
    await loadCourriers(1, combinedFilters);
  }, [loadCourriers]);

  const loadMore = useCallback(async () => {
    if (page < totalPages && !isLoading) {
      try {
        setIsLoading(true);
        const nextPage = page + 1;
        const response: CourrierListResponse = await courrierService.getCourriers(
          filters,
          nextPage,
          limit,
        );

        setCourriers(prev => [...prev, ...response.data]);
        setPage(response.page);
      } catch (err: any) {
        console.error('Erreur lors du chargement de plus de courriers:', err);
        setError(err.message || 'Erreur lors du chargement');
      } finally {
        setIsLoading(false);
      }
    }
  }, [page, totalPages, isLoading, filters, limit]);

  const createCourrier = useCallback(async (data: CreateCourrierRequest): Promise<Courrier> => {
    try {
      setError(null);
      const newCourrier = await courrierService.createCourrier(data);
      await refreshCourriers(); // Recharger la liste
      return newCourrier;
    } catch (err: any) {
      console.error('Erreur lors de la création du courrier:', err);
      setError(err.message || 'Erreur lors de la création');
      throw err;
    }
  }, [refreshCourriers]);

  const updateCourrier = useCallback(async (id: number, data: UpdateCourrierRequest): Promise<Courrier> => {
    try {
      setError(null);
      const updatedCourrier = await courrierService.updateCourrier(id, data);

      // Mettre à jour le courrier dans la liste locale
      setCourriers(prev =>
        prev.map(courrier =>
          courrier.id === id ? updatedCourrier : courrier
        )
      );

      return updatedCourrier;
    } catch (err: any) {
      console.error('Erreur lors de la mise à jour du courrier:', err);
      setError(err.message || 'Erreur lors de la mise à jour');
      throw err;
    }
  }, []);

  const deleteCourrier = useCallback(async (id: number): Promise<void> => {
    try {
      setError(null);
      await courrierService.deleteCourrier(id);

      // Supprimer le courrier de la liste locale
      setCourriers(prev => prev.filter(courrier => courrier.id !== id));
      setTotal(prev => prev - 1);
    } catch (err: any) {
      console.error('Erreur lors de la suppression du courrier:', err);
      setError(err.message || 'Erreur lors de la suppression');
      throw err;
    }
  }, []);

  const assignCourrier = useCallback(async (id: number, data: AssignCourrierRequest): Promise<Courrier> => {
    try {
      setError(null);
      const updatedCourrier = await courrierService.assignCourrier(id, data);

      // Mettre à jour le courrier dans la liste locale
      setCourriers(prev =>
        prev.map(courrier =>
          courrier.id === id ? updatedCourrier : courrier
        )
      );

      return updatedCourrier;
    } catch (err: any) {
      console.error('Erreur lors de l\'assignation du courrier:', err);
      setError(err.message || 'Erreur lors de l\'assignation');
      throw err;
    }
  }, []);

  const transitionCourrier = useCallback(async (id: number, data: TransitionCourrierRequest): Promise<Courrier> => {
    try {
      setError(null);
      const updatedCourrier = await courrierService.transitionCourrier(id, data);

      // Mettre à jour le courrier dans la liste locale
      setCourriers(prev =>
        prev.map(courrier =>
          courrier.id === id ? updatedCourrier : courrier
        )
      );

      return updatedCourrier;
    } catch (err: any) {
      console.error('Erreur lors de la transition du courrier:', err);
      setError(err.message || 'Erreur lors de la transition');
      throw err;
    }
  }, []);

  const reset = useCallback(() => {
    setCourriers([]);
    setTotal(0);
    setPage(1);
    setTotalPages(0);
    setError(null);
    setFilters(initialFilters);
  }, [initialFilters]);

  // Charger automatiquement les courriers au montage et quand les filtres changent
  useEffect(() => {
    if (autoLoad) {
      loadCourriers(1, filters);
    }
  }, [filters, loadCourriers, autoLoad]);

  return {
    courriers,
    total,
    page,
    limit,
    totalPages,
    isLoading,
    error,
    createCourrier,
    updateCourrier,
    deleteCourrier,
    assignCourrier,
    transitionCourrier,
    refreshCourriers,
    searchCourriers,
    loadMore,
    reset,
  };
}